/** A franchise licensee's copy of its parent's notifications (P7 `copy_notification_to_own`), as
 *  the requester's preview recorded it (`preview_summary.franchise`): what the licensee stops
 *  receiving, the copies that replace it (and any created disabled), the parent integrations that
 *  stop running, the fixed recipients the copies keep and the parent rows they keep using. Shown
 *  on the proposal review page, so the person applying it sees the whole effect.
 *
 *  A viewer who does not read the franchise parent's notifications gets it `redacted` (P7, the
 *  dashboard contract's per-viewer shape: empty lists, `counts`, a counting note) and sees counts
 *  only. Ops -- every console user -- always get the full lists; the console handles both. */
import { Box, Typography } from '@mui/material';

import type { AIProposalFranchiseSummary } from '@/api/aiEdit';
import { SectionCard } from '@/components/ReadView';

/** One line per entry, keyed by the entry's ids (two entries may read alike). */
export type Line = { key: string; text: string };

/** A caption over its lines, "—" when there are none (the settings change summary's too). */
export function Lines({ label, lines }: { label: string; lines: Line[] }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      {lines.length === 0 ? (
        <Typography variant="body2">—</Typography>
      ) : (
        lines.map((line) => (
          <Typography key={line.key} variant="body2">
            {line.text}
          </Typography>
        ))
      )}
    </Box>
  );
}

/** The per-viewer redaction of the dashboard contract (`AIProposalFranchiseSummary.redacted` /
 *  `counts` in dashboard_ai_edit.openapi.yaml); never sent to ops, so not in the console contract. */
type Counts = {
  stops_receiving: number;
  copies: number;
  disabled_copies: number;
  not_copied: number;
  fixed_recipients: number;
  kept_parent_references: number;
};
type Shown = AIProposalFranchiseSummary & { redacted?: boolean; counts?: Partial<Counts> };

const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
export const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

function CountsOnly({
  note,
  counts,
  notShown,
}: {
  note: string;
  counts: Partial<Counts>;
  notShown: number;
}) {
  const copies = n(counts.copies);
  const disabled = n(counts.disabled_copies);
  const rows: [string, string][] = [
    ['Stops receiving (the parent’s)', plural(n(counts.stops_receiving), 'notification', 'notifications')],
    [
      'Replaced by (the licensee’s copies)',
      plural(copies, 'copy', 'copies') + (disabled ? `, ${disabled} created disabled` : ''),
    ],
    ['Not copied: stop running for the licensee', plural(n(counts.not_copied), 'delivery method', 'delivery methods')],
    ['Fixed recipients (keep receiving)', plural(n(counts.fixed_recipients), 'recipient', 'recipients')],
    ['Parent rows the copies keep using', plural(n(counts.kept_parent_references), 'row', 'rows')],
  ];
  return (
    <>
      {note && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {note}
        </Typography>
      )}
      {rows.map(([label, text]) => (
        <Lines
          key={label}
          label={label}
          lines={[
            { key: label, text },
            ...(label === 'Parent rows the copies keep using' ? notShownLine(notShown) : []),
          ]}
        />
      ))}
    </>
  );
}

/** P7 round 7: the parent's tags / tag categories a copy keeps are counted, not named, for a viewer
 *  (or requester) without parent-level tag authority (`kept_parent_references_not_shown`). */
const notShownLine = (count: number): Line[] =>
  count > 0
    ? [
        {
          key: 'not-shown',
          text:
            count === 1
              ? '1 kept reference to franchise rows is not shown'
              : `${count} kept references to franchise rows are not shown`,
        },
      ]
    : [];

/** "Email (delivery method #5) " -- a delivery method's entry names it, like the not-copied ones. */
const method = (channel: string | undefined, dm: number | undefined) =>
  channel ? `${channel}${dm !== undefined ? ` (delivery method #${dm})` : ''} ` : '';

export function FranchiseCopySummary({ franchise: summary }: { franchise: AIProposalFranchiseSummary }) {
  const raw = summary as Shown;
  if (raw.redacted) {
    return (
      <Box data-franchise-copy data-franchise-redacted>
        <SectionCard title="Copy to own (franchise)">
          <CountsOnly
            note={raw.note ?? ''}
            counts={raw.counts ?? {}}
            notShown={n(raw.kept_parent_references_not_shown)}
          />
        </SectionCard>
      </Box>
    );
  }
  // The contract requires every list; an older or malformed record must still leave the page
  // (the only place to apply the change) standing.
  const franchise = {
    ...raw,
    stops_receiving: raw.stops_receiving ?? [],
    replaced_by: raw.replaced_by ?? [],
    not_copied: raw.not_copied ?? [],
    fixed_recipients: raw.fixed_recipients ?? [],
    kept_parent_references: raw.kept_parent_references ?? [],
  };
  const notShown = n(raw.kept_parent_references_not_shown);
  return (
    <Box data-franchise-copy>
      <SectionCard title="Copy to own (franchise)">
        {franchise.note && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {franchise.note}
          </Typography>
        )}
        <Lines
          label="Stops receiving (the parent’s)"
          lines={franchise.stops_receiving.map((n) => ({
            key: `${n.id}`,
            text: `#${n.id} ${n.name} · ${n.event}`,
          }))}
        />
        <Lines
          label="Replaced by (the licensee’s copies)"
          lines={franchise.replaced_by.map((c) => ({
            key: `${c.copy_of}`,
            text:
              `${c.name} · copy of #${c.copy_of}` +
              (c.enabled
                ? ''
                : c.why_disabled
                  ? ` — created disabled: ${c.why_disabled}`
                  : ' — disabled, as the parent’s is'),
          }))}
        />
        {franchise.not_copied.length > 0 && (
          <Lines
            label="Not copied: stop running for the licensee"
            lines={franchise.not_copied.map((m) => ({
              key: `${m.notification}:${m.delivery_method}`,
              text:
                `#${m.notification} ${m.name} · ${m.channel} (delivery method #${m.delivery_method}): ` +
                `${m.reason} — ${(m.fields ?? []).join(', ')}`,
            }))}
          />
        )}
        {franchise.fixed_recipients.length > 0 && (
          <Lines
            label="Fixed recipients (keep receiving)"
            lines={franchise.fixed_recipients.map((r) => ({
              key: `${r.notification}:${r.delivery_method}:${r.field}`,
              text: `#${r.notification} · ${method(r.channel, r.delivery_method)}${r.field}: ${r.value}`,
            }))}
          />
        )}
        {(franchise.kept_parent_references.length > 0 || notShown > 0) && (
          <Lines
            label="Parent rows the copies keep using"
            lines={[
              ...franchise.kept_parent_references.map((k) => ({
                key: `${k.notification}:${k.delivery_method ?? ''}:${k.field}:${k.row}`,
                text:
                  `#${k.notification} ${k.name} · ${method(k.channel, k.delivery_method)}${k.field}: ` +
                  `${k.label} (#${k.row})`,
              })),
              ...notShownLine(notShown),
            ]}
          />
        )}
      </SectionCard>
    </Box>
  );
}
